import React, { useEffect, useState } from 'react'
import { defaultSettings } from '../storage'
import { useTracker } from '../context/TrackerContext'
import type { EntryType, Settings, UserAccount, UserPayload, UserRole, UserSex, UserUpdatePayload } from '../types'

interface SettingsProps {
  onLogout: () => void
  username: string
  role: UserRole
  onPasswordChanged: () => void
  users: UserAccount[]
  onCreateUser: (user: UserPayload) => Promise<void>
  onDeleteUser: (username: string) => Promise<void>
  onUpdateUser: (username: string, updates: UserUpdatePayload) => Promise<void>
}

export default function Settings({
  onLogout,
  username,
  role,
  onPasswordChanged,
  users,
  onCreateUser,
  onDeleteUser,
  onUpdateUser,
}: SettingsProps) {
  const { settings, saveSettings } = useTracker()
  const [draft, setDraft] = useState<Settings>(normalizeSettings(settings))
  const [status, setStatus] = useState<string>()
  const [saving, setSaving] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [passwordStatus, setPasswordStatus] = useState<string>()
  const [passwordSaving, setPasswordSaving] = useState(false)

  useEffect(() => {
    setDraft(normalizeSettings(settings))
  }, [settings])

  function updateCategory(type: EntryType, idx: number, value: string) {
    setDraft(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [type]: (prev.categories?.[type] ?? []).map((c, i) => (i === idx ? value : c)),
      },
    }))
  }

  function addCategory(type: EntryType) {
    setDraft(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [type]: [...(prev.categories?.[type] ?? []), 'New item'],
      },
    }))
  }

  function removeCategory(type: EntryType, idx: number) {
    setDraft(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [type]: (prev.categories?.[type] ?? []).filter((_, i) => i !== idx),
      },
    }))
  }

  async function save() {
    if (saving) return
    setSaving(true)
    setStatus(undefined)
    try {
      await saveSettings(draft)
      setStatus('Settings saved')
    } catch (err) {
      setStatus((err as Error).message || 'Unable to save settings')
    } finally {
      setSaving(false)
    }
  }

  async function reset() {
    if (saving) return
    setSaving(true)
    try {
      setDraft(defaultSettings)
      await saveSettings(defaultSettings)
      setStatus('Reset to defaults')
    } catch (err) {
      setStatus((err as Error).message || 'Unable to reset settings')
    } finally {
      setSaving(false)
    }
  }

  async function changePassword() {
    const trimmed = newPassword.trim()
    setPasswordSaving(true)
    setPasswordStatus(undefined)
    try {
      await onUpdateUser(username, { password: trimmed })
      setNewPassword('')
      setPasswordStatus(trimmed ? 'Password updated' : 'Password cleared')
      onPasswordChanged()
    } catch (err) {
      setPasswordStatus((err as Error).message || 'Unable to update password')
    } finally {
      setPasswordSaving(false)
    }
  }

  return (
    <div>
      <h3>Settings</h3>
      {status && <div className="status info">{status}</div>}
      <div>
        {(['did', 'ate', 'drank', 'other'] as const).map(type => {
          const items = draft.categories?.[type] ?? []
          return (
          <div key={type} className="settings-section">
            <h4>{labelForType(type)}</h4>
            {items.map((c, idx) => (
              <div key={`${type}-${idx}`} className="setting-row">
                <input value={c} onChange={e => updateCategory(type, idx, e.target.value)} />
                <button onClick={() => removeCategory(type, idx)}>Remove</button>
              </div>
            ))}
            <button onClick={() => addCategory(type)}>Add category</button>
          </div>
        )})}
      </div>

      <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button className="button" onClick={save} disabled={saving}>Save</button>
        <button style={{ marginLeft: 0 }} onClick={reset} disabled={saving}>Reset defaults</button>
        <button className="button danger" type="button" onClick={onLogout} style={{ marginLeft: 0 }}>
          Log out
        </button>
      </div>

      <div style={{ marginTop: 20 }}>
        <h4>Change password</h4>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="New password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            style={{ flex: 1, minWidth: 200, padding: 8 }}
          />
          <button className="button" type="button" onClick={changePassword} disabled={passwordSaving}>
            {passwordSaving ? 'Updating…' : 'Update password'}
          </button>
        </div>
        {passwordStatus && <div className="status info" style={{ marginTop: 8 }}>{passwordStatus}</div>}
      </div>

      {role === 'admin' && (
        <UserAdmin
          currentUser={username}
          users={users}
          onCreateUser={onCreateUser}
          onDeleteUser={onDeleteUser}
          onUpdateUser={onUpdateUser}
        />
      )}
    </div>
  )
}

const USERNAME_REGEX = /^[a-z0-9_-]+$/

const SEX_OPTIONS: { value: '' | UserSex; label: string }[] = [
  { value: '', label: 'Not specified' },
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'other', label: 'Other' },
]

function isSupportedUsername(value: string) {
  return USERNAME_REGEX.test(value)
}

function interpretAgeInput(value: string): { action: 'set'; value: number } | { action: 'clear' } | { action: 'error'; message: string } {
  const trimmed = (value ?? '').trim()
  if (!trimmed) {
    return { action: 'clear' }
  }
  const num = Number(trimmed)
  if (!Number.isFinite(num) || num <= 0) {
    return { action: 'error', message: 'Age must be a positive number' }
  }
  const rounded = Math.min(120, Math.max(1, Math.round(num)))
  return { action: 'set', value: rounded }
}

function normalizeSexValue(value: string): UserSex | undefined {
  const normalized = (value ?? '').trim().toLowerCase()
  if (normalized === 'female' || normalized === 'male' || normalized === 'other') {
    return normalized
  }
  return undefined
}

function UserAdmin({
  currentUser,
  users,
  onCreateUser,
  onDeleteUser,
  onUpdateUser,
}: {
  currentUser: string
  users: UserAccount[]
  onCreateUser: (user: UserPayload) => Promise<void>
  onDeleteUser: (username: string) => Promise<void>
  onUpdateUser: (username: string, updates: UserUpdatePayload) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    role: 'user' as UserRole,
    appleUser: '',
    googleUser: '',
    age: '',
    sex: '' as '' | UserSex,
  })
  const [status, setStatus] = useState<string>()
  const [pendingPasswords, setPendingPasswords] = useState<Record<string, string>>({})
  const [pendingAppleIds, setPendingAppleIds] = useState<Record<string, string>>({})
  const [pendingGoogleIds, setPendingGoogleIds] = useState<Record<string, string>>({})
  const [pendingAges, setPendingAges] = useState<Record<string, string>>({})
  const [pendingSexes, setPendingSexes] = useState<Record<string, '' | UserSex>>({})
  const [busy, setBusy] = useState(false)

  function handlePendingPassword(username: string, value: string) {
    setPendingPasswords(prev => ({ ...prev, [username]: value }))
  }

  function handlePendingApple(username: string, value: string) {
    setPendingAppleIds(prev => ({ ...prev, [username]: value }))
  }

  function handlePendingGoogle(username: string, value: string) {
    setPendingGoogleIds(prev => ({ ...prev, [username]: value }))
  }

  function handlePendingAge(username: string, value: string) {
    setPendingAges(prev => ({ ...prev, [username]: value }))
  }

  function handlePendingSex(username: string, value: '' | UserSex) {
    setPendingSexes(prev => ({ ...prev, [username]: value }))
  }

  async function addUser() {
    if (!newUser.username.trim()) {
      setStatus('Provide a username')
      return
    }
    if (!isSupportedUsername(newUser.username.trim().toLowerCase())) {
      setStatus('Username may only contain lowercase letters, numbers, hyphen, or underscore')
      return
    }
    const ageResult = interpretAgeInput(newUser.age || '')
    if (ageResult.action === 'error') {
      setStatus(ageResult.message)
      return
    }
    const normalizedSex = normalizeSexValue(newUser.sex || '')
    setBusy(true)
    setStatus(undefined)
    try {
      await onCreateUser({
        username: newUser.username.toLowerCase(),
        password: newUser.password,
        role: newUser.role,
        appleUser: newUser.appleUser.trim() || undefined,
        googleUser: newUser.googleUser.trim() || undefined,
        age: ageResult.action === 'set' ? ageResult.value : undefined,
        sex: normalizedSex,
      })
      setNewUser({ username: '', password: '', role: 'user', appleUser: '', googleUser: '', age: '', sex: '' as '' | UserSex })
      setStatus('User created')
    } catch (err) {
      setStatus((err as Error).message || 'Unable to create user')
    } finally {
      setBusy(false)
    }
  }

  async function updatePassword(username: string) {
    if (!isSupportedUsername(username)) {
      setStatus('This username format is read-only. Update data_runtime/users.json directly.')
      return
    }
    const pwd = (pendingPasswords[username] || '').trim()
    setBusy(true)
    setStatus(undefined)
    try {
      await onUpdateUser(username, { password: pwd })
      handlePendingPassword(username, '')
      setStatus(pwd ? 'Password updated' : 'Password cleared')
    } catch (err) {
      setStatus((err as Error).message || 'Unable to update password')
    } finally {
      setBusy(false)
    }
  }

  async function updateApple(username: string) {
    if (!isSupportedUsername(username)) {
      setStatus('This username format is read-only. Update data_runtime/users.json directly.')
      return
    }
    const raw = (pendingAppleIds[username] ?? users.find(u => u.username === username)?.appleUser ?? '').trim()
    setBusy(true)
    setStatus(undefined)
    try {
      await onUpdateUser(username, { appleUser: raw || '' })
      setStatus(raw ? 'Apple ID updated' : 'Apple ID cleared')
      handlePendingApple(username, raw)
    } catch (err) {
      setStatus((err as Error).message || 'Unable to update Apple ID')
    } finally {
      setBusy(false)
    }
  }

  async function updateGoogle(username: string) {
    if (!isSupportedUsername(username)) {
      setStatus('This username format is read-only. Update data_runtime/users.json directly.')
      return
    }
    const raw = (pendingGoogleIds[username] ?? users.find(u => u.username === username)?.googleUser ?? '').trim()
    setBusy(true)
    setStatus(undefined)
    try {
      await onUpdateUser(username, { googleUser: raw || '' })
      setStatus(raw ? 'Google ID updated' : 'Google ID cleared')
      handlePendingGoogle(username, raw)
    } catch (err) {
      setStatus((err as Error).message || 'Unable to update Google ID')
    } finally {
      setBusy(false)
    }
  }

  async function updateDemographics(username: string) {
    if (!isSupportedUsername(username)) {
      setStatus('This username format is read-only. Update data_runtime/users.json directly.')
      return
    }
    const base = users.find(u => u.username === username)
    const ageInput =
      pendingAges[username] !== undefined ? pendingAges[username] : base?.age !== undefined ? String(base.age) : ''
    const sexInput =
      pendingSexes[username] !== undefined ? pendingSexes[username] : (base?.sex ?? '')
    const ageResult = interpretAgeInput(ageInput || '')
    if (ageResult.action === 'error') {
      setStatus(ageResult.message)
      return
    }
    const normalizedSex = sexInput ? normalizeSexValue(sexInput) : undefined
    if (sexInput && !normalizedSex) {
      setStatus('Select a valid sex option')
      return
    }
    const updates: UserUpdatePayload = {}
    if (ageResult.action === 'set') {
      updates.age = ageResult.value
    } else if (ageResult.action === 'clear' && base?.age !== undefined) {
      updates.age = null
    }
    if (sexInput === '') {
      if (base?.sex) {
        updates.sex = ''
      }
    } else if (normalizedSex) {
      updates.sex = normalizedSex
    }
    if (!Object.keys(updates).length) {
      setStatus('No demographic changes to save')
      return
    }
    setBusy(true)
    setStatus(undefined)
    try {
      await onUpdateUser(username, updates)
      setStatus('Profile updated')
      handlePendingAge(username, ageResult.action === 'set' ? String(ageResult.value) : '')
      handlePendingSex(username, sexInput || '')
    } catch (err) {
      setStatus((err as Error).message || 'Unable to update profile')
    } finally {
      setBusy(false)
    }
  }

  async function updateRole(username: string, role: UserRole) {
    if (!isSupportedUsername(username)) {
      setStatus('This username format is read-only. Update data_runtime/users.json directly.')
      return
    }
    setBusy(true)
    setStatus(undefined)
    try {
      await onUpdateUser(username, { role })
      setStatus('Role updated')
    } catch (err) {
      setStatus((err as Error).message || 'Unable to update role')
    } finally {
      setBusy(false)
    }
  }

  async function remove(username: string) {
    if (!window.confirm(`Remove ${username}?`)) return
    if (!isSupportedUsername(username)) {
      setStatus('This username format is read-only. Update data_runtime/users.json directly.')
      return
    }
    setBusy(true)
    setStatus(undefined)
    try {
      await onDeleteUser(username)
      setStatus('User removed')
    } catch (err) {
      setStatus((err as Error).message || 'Unable to remove user')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="user-admin">
      <button className="button secondary" type="button" onClick={() => setOpen(!open)}>
        {open ? 'Hide user management' : 'Manage users'}
      </button>
      {open && (
        <div className="user-panel">
          <h4>Add user</h4>
          <div className="user-form">
            <input
              placeholder="Username"
              value={newUser.username}
              onChange={e => setNewUser(prev => ({ ...prev, username: e.target.value }))}
            />
          <input
            placeholder="Password"
            value={newUser.password}
            onChange={e => setNewUser(prev => ({ ...prev, password: e.target.value }))}
          />
          <input
            placeholder="Age (optional)"
            type="number"
            min={1}
            max={120}
            value={newUser.age}
            onChange={e => setNewUser(prev => ({ ...prev, age: e.target.value }))}
          />
          <select value={newUser.sex} onChange={e => setNewUser(prev => ({ ...prev, sex: e.target.value as '' | UserSex }))}>
            {SEX_OPTIONS.map(option => (
              <option key={option.value || 'none'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <input
            placeholder="Apple ID (optional)"
            value={newUser.appleUser}
            onChange={e => setNewUser(prev => ({ ...prev, appleUser: e.target.value }))}
          />
            <input
              placeholder="Google ID (optional)"
              value={newUser.googleUser}
              onChange={e => setNewUser(prev => ({ ...prev, googleUser: e.target.value }))}
            />
            <select value={newUser.role} onChange={e => setNewUser(prev => ({ ...prev, role: e.target.value as UserRole }))}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            <button className="button" type="button" onClick={addUser} disabled={busy}>
              Add
            </button>
          </div>
          <h4 style={{ marginTop: 16 }}>Existing users</h4>
          <div className="user-list">
            {users.map(user => (
              <div key={user.username} className="user-row">
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{user.username}</div>
                  <select
                    value={user.role}
                    onChange={e => updateRole(user.username, e.target.value as UserRole)}
                    disabled={busy || !isSupportedUsername(user.username)}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="user-actions">
                  <input
                    placeholder="New password"
                    value={pendingPasswords[user.username] || ''}
                    onChange={e => handlePendingPassword(user.username, e.target.value)}
                    disabled={!isSupportedUsername(user.username)}
                  />
                  <button className="button small" type="button" onClick={() => updatePassword(user.username)} disabled={busy || !isSupportedUsername(user.username)}>
                    Set password
                  </button>
                  <input
                    placeholder="Apple ID"
                    value={pendingAppleIds[user.username] ?? user.appleUser ?? ''}
                    onChange={e => handlePendingApple(user.username, e.target.value)}
                    disabled={!isSupportedUsername(user.username)}
                  />
                  <button className="button small" type="button" onClick={() => updateApple(user.username)} disabled={busy || !isSupportedUsername(user.username)}>
                    Save Apple ID
                  </button>
                  <input
                    placeholder="Google ID"
                    value={pendingGoogleIds[user.username] ?? user.googleUser ?? ''}
                    onChange={e => handlePendingGoogle(user.username, e.target.value)}
                    disabled={!isSupportedUsername(user.username)}
                  />
                  <button className="button small" type="button" onClick={() => updateGoogle(user.username)} disabled={busy || !isSupportedUsername(user.username)}>
                    Save Google ID
                  </button>
                  <input
                    type="number"
                    placeholder="Age"
                    min={1}
                    max={120}
                    value={pendingAges[user.username] ?? (user.age !== undefined ? String(user.age) : '')}
                    onChange={e => handlePendingAge(user.username, e.target.value)}
                    disabled={!isSupportedUsername(user.username)}
                  />
                  <select
                    value={pendingSexes[user.username] ?? (user.sex ?? '')}
                    onChange={e => handlePendingSex(user.username, e.target.value as '' | UserSex)}
                    disabled={!isSupportedUsername(user.username)}
                  >
                    {SEX_OPTIONS.map(option => (
                      <option key={option.value || 'none'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    className="button small"
                    type="button"
                    onClick={() => updateDemographics(user.username)}
                    disabled={busy || !isSupportedUsername(user.username)}
                  >
                    Save profile
                  </button>
                  <button
                    className="button danger small"
                    type="button"
                    onClick={() => remove(user.username)}
                    disabled={busy || user.username === currentUser || !isSupportedUsername(user.username)}
                  >
                    Remove
                  </button>
                </div>
                {!isSupportedUsername(user.username) && (
                  <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                    Username includes unsupported characters; edit this user manually in the data files if needed.
                  </div>
                )}
              </div>
            ))}
          </div>
          {status && <div className="status info" style={{ marginTop: 8 }}>{status}</div>}
        </div>
      )}
    </div>
  )
}

function normalizeSettings(settings: Settings): Settings {
  const fallback = { did: [], ate: [], drank: [], other: [] }
  return {
    categories: {
      did: settings.categories?.did ?? [],
      ate: settings.categories?.ate ?? [],
      drank: settings.categories?.drank ?? [],
      other: settings.categories?.other ?? [],
    },
  }
}

function labelForType(type: EntryType) {
  if (type === 'did') return 'Did'
  if (type === 'ate') return 'Ate'
  if (type === 'drank') return 'Drank'
  return 'Other'
}
